import clsx from 'clsx'
import React from 'react'
import { ControllerRenderProps, FieldValues } from 'react-hook-form'
import { FormattedMessage } from 'react-intl'

import s from './styles.module.scss'

import { DatePickerModeEnum } from './date-picker.enum'
import {
  DateSearchBarPicker,
  DateSearchBarPickerProps,
} from './DateSearchBarPicker'
import { Icon, SearchBarPicker } from '../../atoms'
import { SearchBarPickerModeEnum } from '../../atoms/fields/search-bar-picker/search-bar-picker.enum'
import { useTravelTypeContext } from '../../contexts'
import { TravelTypeEnum } from '../../../lib/graphql'
import { useDateFormatter } from '../../../lib/hooks'
import { cn } from '../../../shadcn/utils'

interface DatePickerInputFieldProps {
  mode: DatePickerModeEnum
  isModalOpen: boolean
  revampPicker: boolean
  isDatesSelected: boolean

  isMobile: boolean
  hideLabels?: boolean

  field: ControllerRenderProps<FieldValues, string>

  alternativeClassName?: string
}

interface InputPickerContainerProps {
  containerClassName?: string
  inputClassName: string
  inputStyle?: React.CSSProperties
  isDatesSelected: boolean
  searchBarPickerProps: Omit<DateSearchBarPickerProps, 'isDatesSelected'>
}

const InputPickerContainer: React.FC<
  React.PropsWithChildren<InputPickerContainerProps>
> = ({
  containerClassName,
  inputClassName,
  isDatesSelected,
  searchBarPickerProps,
  inputStyle,
  children,
}) => (
  <div className={containerClassName}>
    <DateSearchBarPicker
      className={inputClassName}
      inputStyle={inputStyle}
      isDatesSelected={isDatesSelected}
      {...searchBarPickerProps}
    />
    {children}
  </div>
)

const shouldRenderChildren = (
  allowMultiDates: boolean,
  revampPicker: boolean,
  mode: DatePickerModeEnum,
): boolean => {
  if (!allowMultiDates) {
    return false
  }
  if (mode === DatePickerModeEnum.HOUSE_SEARCH && revampPicker) {
    return false
  }
  return true
}

export const DatePickerInputField: React.FC<DatePickerInputFieldProps> = ({
  mode,
  isModalOpen,
  revampPicker,
  isDatesSelected,
  isMobile,
  hideLabels,
  field,
  alternativeClassName,
}) => {
  const { travelType } = useTravelTypeContext()
  const dateFormatter = useDateFormatter()

  const searchBarPickerMode =
    mode === DatePickerModeEnum.HOUSE_SEARCH
      ? SearchBarPickerModeEnum.HOUSE_SEARCH
      : undefined
  const isCorporateEveningEvent =
    travelType === TravelTypeEnum.CORPORATE_EVENING_EVENT
  const allowMultiDates = !isCorporateEveningEvent

  const inputClassName = clsx(s.inputDate, { [s.focused]: isModalOpen })
  const inputStyle =
    !allowMultiDates || revampPicker ? { minWidth: '135px' } : undefined

  const commonProps: InputPickerContainerProps = {
    inputClassName,
    isDatesSelected,
    inputStyle,
    searchBarPickerProps: {
      isMobile,
      alternativeClassName,
      mode,
      allowMultiDates,
      isCorporateEveningEvent,
      revampPicker,
      hideLabels,
      fieldValue: field.value,
    },
  }

  const containerClassName = clsx(
    mode === DatePickerModeEnum.HOUSE_SEARCH
      ? s.houseSearchContainerInputDates
      : s.containerInputDates,
    {
      [s.mobile]: isMobile,
      [s.singleInput]:
        revampPicker ||
        !allowMultiDates ||
        mode != DatePickerModeEnum.HOUSE_SEARCH,
    },
  )

  return (
    <InputPickerContainer
      containerClassName={containerClassName}
      {...commonProps}
    >
      {shouldRenderChildren(allowMultiDates, revampPicker, mode) && (
        <div className={inputClassName}>
          {mode === DatePickerModeEnum.HOUSE_SEARCH && (
            <div
              className={cn(
                isDatesSelected || !!field.value?.endDate
                  ? 'text-black'
                  : 'text-grey-600',
              )}
            >
              <Icon name='calendar' size={15} />
            </div>
          )}
          <SearchBarPicker
            label={
              mode !== DatePickerModeEnum.HOUSE_SEARCH ? (
                <FormattedMessage
                  id='ui.DatePicker.departure'
                  defaultMessage='Départ'
                />
              ) : undefined
            }
            placeholder={
              <FormattedMessage
                id='ui.DatePicker.to'
                defaultMessage='À quand ?'
              />
            }
            value={
              field.value?.endDate
                ? dateFormatter.format({
                    date: field.value.endDate,
                    formatString: 'dayMonth',
                    useUTC: true,
                  })
                : ''
            }
            isMobile={isMobile}
            alternativeClassName={alternativeClassName}
            mode={searchBarPickerMode}
          />
        </div>
      )}
    </InputPickerContainer>
  )
}
